import * as React from 'react';
import _ from 'lodash';
import { Row, Col, Label, Button } from 'reactstrap';
import ReactGA from 'react-ga';
import { Form } from '../../../share/components/Form/Form';
import { WrappedInput } from '../../../share/components/Input/Input';
import { toastr } from 'react-redux-toastr';
import { CasesService } from '../../../share/services/cases';
import './closeCase.css';
import { timestampToMoment } from '../../../share/utils/utils';
import { ICase, closeCaseTypes, CloseCaseReason } from '../../../share/types/case/case';
import { initialCase } from '../../../share/types/case/case';
import { PulseLoader } from 'react-spinners';
import { IReduxState } from 'src/reduxTypes';
import { connect } from 'react-redux';
import { IAuthState } from 'src/modules/Auth/store/types';
import { History } from 'history';
import { match } from 'react-router';
import { permissionsCase, isAnETLCase } from 'src/share/utils/permissionsCase';

export interface ICloseCaseFormUIState {
  caseInfo: ICase;
  loading: boolean,
  submitted: boolean;
  beforeUpdate: ICase | null;
  loadingMessage: string;
};

export interface ICloseCaseFormUIProps {
  history: History;
  match: match<{ caseId: string }>;
  auth: IAuthState
};



export class CloseCaseForm extends React.Component<ICloseCaseFormUIProps, ICloseCaseFormUIState> {
  public newCase: ICase;
  constructor(props: ICloseCaseFormUIProps) {
    super(props);
    this.state = {
      caseInfo: initialCase,
      loading: true,
      submitted: false,
      beforeUpdate: null,
      loadingMessage: '',
    }
  }

  public componentDidMount() {
    try {
      this.loadCase();
    } catch (ex) {
      toastr.error('Error', 'There was a problem loading data from the case');
      this.setState({ loading: false });
    }
  }

  public loadCase = async () => {
    this.setState({ loading: true });
    const caseId = this.props.match.params.caseId;
    const date = timestampToMoment(new Date().getTime());
    try {
      const res = await CasesService.getCaseByID(caseId);
      if (!res) {
        throw { code: 1 }
      }
      if (!permissionsCase(res.agencyCode, this.props.auth.agencyCode, []) || isAnETLCase(res)) {
        this.props.history.replace('/')
        toastr.error('Error', 'You don\'t have permission to see this page');
      }
      this.setState({
        beforeUpdate: _.cloneDeep(res),
        caseInfo: { ...res, closedDate: date, status: 'closed' },
        loading: false,
      });
    }
    catch (error) {
      toastr.error('Error', 'There was a problem loading the case data');
      this.setState({ loading: false });
    }
  }

  public setValue = (path: string, value: any) => {
    const state = { ...this.state };
    _.set(state, path, value)
    this.setState(state);
  }

  public handleSubmit = () => {
    this.setValue('submitted', true);
  }

  public handleValidSubmit = async () => {
    if (this.state.beforeUpdate) {
      try {
        await this.save();
      } catch (error) {
        this.setState({ loading: false, loadingMessage: '' });
        toastr.error('Error', 'There was a problem connecting to the server');
      }
    }
  }

  public async save() {
    this.setState({ loading: true, loadingMessage: 'Closing Case' });
    await CasesService.update(_.cloneDeep(this.state.beforeUpdate), this.state.caseInfo);
    this.savedCorrectly();
  }

  public handleInvalidSubmit = () => {
    toastr.error('Close Case', 'Please fill all the required values');
  }

  public savedCorrectly() {
    toastr.success('Close Case', 'The case has been closed correctly');
    ReactGA.event({
      category: 'Case',
      action: 'Closed case',
      label: 'Cases management'
    });
    this.props.history.replace('/');
  }

  public render() {
    const { caseInfo, loading } = this.state;

    return (
      <div className="close-case case content">
        <Form
          onInvalidSubmit={this.handleInvalidSubmit}
          onValidSubmit={this.handleValidSubmit}
          onSubmit={this.handleSubmit}
        >
          <div className="margin-forms white-background">
            <Row>
              <Col md={12} className="sectionTitle">
                <h2 className="pt-large-icon">
                  Closing Case {this.props.match.params.caseId}
                </h2>
                <p>Please update the following fields, then click 'Submit'.</p>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Label className="input-label">Case Solved Date</Label>
              </Col>
              <br />
              <WrappedInput
                type="date"
                value={caseInfo.closedDate}
                name={`caseInfo.closedDate`}
                md={3}
                inputinfo="Must be in day/month/year format"
                onChange={(e: any) => this.setValue(`caseInfo.closedDate`, e.currentTarget.value)}
                required={true}
                disabled={loading}
              />
            </Row>
            <Row>
              <Col md={12}>
                <Label className="input-label">Reason</Label>
              </Col>
              <WrappedInput
                type="select"
                placeholder="Reason"
                md={3}
                name={`caseInfo.closeReason`}
                value={caseInfo.closeReason}
                onChange={(e: any) => this.setValue(`caseInfo.closeReason`, e.currentTarget.value)}
                required={true}
                disabled={loading}
              >
                <option value="">Why are you closing this case?</option>
                {
                  Object.keys(closeCaseTypes).map((key) => {
                    return (
                      <option key={key} value={CloseCaseReason[key]}>{closeCaseTypes[key]}</option>
                    )
                  })
                }
              </WrappedInput>
            </Row>
            <Row>
              <WrappedInput
                id="missing"
                md={12}
                type="textarea"
                style={{ height: 150 }}
                value={caseInfo.circumstances}
                placeholder="Missing Circumstances"
                onChange={(e: any) => this.setValue(`caseInfo.circumstances`, e.target.value)}
                disabled={loading}
              />
            </Row>
            <Row>
              <WrappedInput
                id="comments"
                md={12}
                type="textarea"
                style={{ height: 150 }}
                value={caseInfo.closedComments}
                placeholder="Case Manager Comments"
                onChange={(e: any) => this.setValue(`caseInfo.closedComments`, e.target.value)}
                disabled={loading}
              />
            </Row>
            <Row>
              <Col md="8" className="offset-4 text-center">
                {this.renderSubmitView()}
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    );
  }

  public renderSubmitView() {
    return this.state.loading ?
      <>
        <Label id="loadingText" className="text-center saving-text">{this.state.loadingMessage}</Label>
        <PulseLoader size={15} color={'#db8733'} loading={this.state.loading} />
      </> :
      <Button type="submit" id="submitBTN" className="btn btn-outline float-right">
        Submit
      </Button>
  }
}
const mapStateToProps = (state: IReduxState) => ({
  auth: state.auth
});
export default connect(mapStateToProps)(CloseCaseForm);
